import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CellValueChangedEvent,
  GridApi,
  GridReadyEvent,
  IRowNode,
} from 'ag-grid-community'
import { useDispatch, useSelector } from 'react-redux'
import { PageArea, PageProps, APPLICATION_FUNCTION_EXTERNAL_ID } from '..'
import { AllState } from '../../../store'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { pageComponent } from '../../higher-order-components/pageComponent'
import Loading from '../../components/process-state-notifications/Loading'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import { UiStateKey } from '../../../lib/commons/uiStates'
import {
  ProgramManagementGridContext,
  ProgramManagementRow,
  PROGRAM_MANAGEMENT_ROW_TYPE,
} from './programManagement'
import { programItemRowService } from './programItemRowService'
import { Header } from './components/Header'
import { GanttChartProvider } from '../../containers/BulkSheetView/GanttChartProvider'
import { ProgressLineOverlay } from '../../containers/commons/AgGrid/components/cell/custom/gantt/ProgressLineOverlay'
import { usePageState } from './hooks/usePageState'
import { useProgramManagementData } from './hooks/useProgramManagementData'
import { useProgramManagementBulkSheetState } from './hooks/useBulkSheetState'
import { useProgramManagementGridOptions } from './gridOptions'
import { useGanttChart } from './hooks/useGanttChart'
import { useCrossProjectCriticalPathData } from './hooks/useCrossProjectCriticalPathData'
import { useProgramCriticalPathData } from './hooks/useProgramCriticalPathData'
import { AddCriticalPathLinkInput } from '../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/GanttCriticalPath/CriticalPathContext'
import { getGanttRowItem } from './gridOptions/columnDefs/ganttColumn'
import { useWorkloadUnit } from '../../hooks/useWorkloadUnit'
import { featureFlagService } from '../../../utils/feature-flag'
import {
  WbsItemLinkDialog,
  WbsItemLinkFilterOption,
  WbsItemLinkSummary,
  WbsItemLinkSource,
  WBS_ITEM_LINK_SOURCE,
  isProgramSource,
} from '../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/WbsItemLink/WbsItemLinkDialog'
import { intl } from '../../../i18n'
import {
  GANTT_FIT_MIN_CONTENT_RATIO,
  getGanttChartWidth,
} from '../ProjectPlan/projectPlanOptions'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import { DeleteRowConfirmationDialog } from '../../containers/BulkSheetView/components/dialog/DeleteRowConfirmationDialog'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import { ProjectSelectionDialog } from './components/ProjectSelectionDialog'
import { openProjectEditDialog } from '../ProjectV2/openProjectEditDialog'
import { fromDetail } from '../ProjectV2/model'
import { fetchProjectDetailByUuid } from '../../../infrastructure/api/project'
import { useProgramManagementContextMenu } from './hooks/useContextMenu'
import { useProgramManagementDragAndDrop } from './hooks/useDragAndDrop'
import { useProgramManagementDialog } from './hooks/useDialog'
import { useGridContext } from '../../containers/BulkSheetView/hooks/useGridContext'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { requireSave, doNotRequireSave } from '../../../store/requiredSaveData'
import {
  addGlobalMessage,
  addScreenMessage,
  MessageLevel,
} from '../../../store/messages'
import { handleWarning } from '../../../handlers/globalErrorHandler'
import { extractValuesFromResponse } from '../../../lib/commons/api'
import {
  getSelectedNode,
  getRowNumber,
} from '../../containers/BulkSheetView/lib/gridApi'
import { getParentUuid } from '../../containers/BulkSheetView/lib/tree'
import {
  InputError,
  validateNodes,
} from '../../containers/BulkSheetView/lib/validation'

const GANTT_MIN_ROW_HEIGHT = 49

const FILTER_ALL = '__all__'
const FILTER_PROGRAM = '__program__'

type ProgramManagementProps = PageProps & {
  code?: string
}

const ProgramManagementPage = ({
  uuid: functionUuid,
  code,
}: ProgramManagementProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [gridApi, setGridApi] = useState<GridApi<ProgramManagementRow> | null>(
    null
  )
  const dispatch = useDispatch()
  // プロジェクト単票ダイアログは PROJECT_REGISTRATION 権限が前提（無いと中身が空に
  // なる）。権限が無いユーザーには新規追加メニュー・アイコンクリックの導線を出さない。
  const canRegisterProject = useSelector((state: AllState) =>
    state.appFunction.functions.some(
      f =>
        f.externalId === APPLICATION_FUNCTION_EXTERNAL_ID.PROJECT_REGISTRATION
    )
  )
  const projectEditDialogEnabled =
    featureFlagService.isEnabled('project-v2') && canRegisterProject

  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api as GridApi<ProgramManagementRow>)
  }, [])

  const pageStateApi = usePageState(functionUuid)
  const rowHeightBeforeGantt = useRef<number | null>(null)
  const dataApi = useProgramManagementData(
    pageStateApi.selectedProgramUuid,
    pageStateApi.setSelectedProgramUuid,
    code
  )

  // プロジェクト行のアイコンクリックで projectEdit ダイアログを開く。
  // 新規下書きは再編集、既存プロジェクトは detail を取得して編集（保存時に delta 算出）。
  // updateProjectRow は安定（useCallback []）。dataApi 全体に依存すると毎レンダー
  // 再生成され gridOptions の useMemo が無効化されるため、関数のみに依存させる。
  const { updateProjectRow } = dataApi
  const onProjectIconClick = useCallback(
    async (row: ProgramManagementRow) => {
      if (row.isNewProject) {
        openProjectEditDialog({
          initial: row.projectDraft,
          isNew: true,
          onComplete: formModel => updateProjectRow(row.uuid, formModel),
        })
        return
      }
      if (!row.projectUuid) return
      const detail =
        row.projectOriginal ?? (await fetchProjectDetailByUuid(row.projectUuid))
      openProjectEditDialog({
        initial: row.projectDraft ?? fromDetail(detail),
        isNew: false,
        onComplete: formModel => updateProjectRow(row.uuid, formModel, detail),
      })
    },
    [updateProjectRow]
  )

  const gridOptions = useProgramManagementGridOptions(
    projectEditDialogEnabled ? onProjectIconClick : undefined
  )
  const bulkSheetStateApi = useProgramManagementBulkSheetState(
    gridApi,
    functionUuid
  )
  const columnSetting = useColumnSetting()
  const dialog = useProgramManagementDialog()

  const { contextRef, updateContext } = useGridContext(
    gridApi ?? undefined,
    gridOptions.context ?? {}
  )

  const { timeScale, displayTimeScale } = useGanttChart(
    pageStateApi.ganttParameter,
    pageStateApi.ganttVisible
  )

  const workloadUnitState = useWorkloadUnit(pageStateApi.workloadUnit)

  const isDependencyCriticalPathEnabled = featureFlagService.isEnabled(
    'gantt-dependency-critical-path'
  )

  const crossProjectCpData = useCrossProjectCriticalPathData(
    dataApi.data,
    gridApi
  )

  // クロスプロジェクトCPのUUID集合（重複排除用）
  const crossProjectCpUuids = useMemo(
    () => new Set(crossProjectCpData.criticalPaths.map(cp => cp.uuid)),
    [crossProjectCpData.criticalPaths]
  )

  const programCpData = useProgramCriticalPathData(
    pageStateApi.selectedProgramUuid,
    dataApi.data,
    gridApi,
    crossProjectCpUuids
  )
  const [criticalPathDialogOpen, setCriticalPathDialogOpen] = useState(false)
  const [cpFilterValue, setCpFilterValue] = useState(FILTER_ALL)

  // 両CPソースのマージ（UUID重複を排除）
  const mergedCriticalPaths = useMemo(
    () => [
      ...crossProjectCpData.criticalPaths,
      ...programCpData.criticalPaths.filter(
        cp => !crossProjectCpUuids.has(cp.uuid)
      ),
    ],
    [
      crossProjectCpData.criticalPaths,
      programCpData.criticalPaths,
      crossProjectCpUuids,
    ]
  )

  const mergedWbsItemInfoMap = useMemo(() => {
    const merged = new Map(crossProjectCpData.wbsItemInfoMap)
    for (const [key, value] of programCpData.wbsItemInfoMap) {
      merged.set(key, value)
    }
    return merged
  }, [crossProjectCpData.wbsItemInfoMap, programCpData.wbsItemInfoMap])

  // プロジェクト情報マップの構築: rows から PROJECT 行を抽出
  const projectInfoMap = useMemo(() => {
    const map = new Map<
      string,
      { name: string; iconUrl?: string; projectUuid: string }
    >()
    for (const row of dataApi.data) {
      if (row.type === PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT && row.projectUuid) {
        map.set(row.projectUuid, {
          name: row.displayName ?? '',
          iconUrl: row.iconUrl,
          projectUuid: row.projectUuid,
        })
      }
    }
    return map
  }, [dataApi.data])

  // rows から projectPlanUuid → projectUuid のマッピングを構築
  // WORKGROUP/PROCESS 行は treeValue を辿って祖先の PROJECT 行を解決する
  const projectPlanToProjectUuid = useMemo(() => {
    const map = new Map<string, string>()
    const rowByUuid = new Map<string, ProgramManagementRow>()
    for (const row of dataApi.data) {
      rowByUuid.set(row.uuid, row)
    }
    for (const row of dataApi.data) {
      if (row.projectUuid) {
        map.set(row.uuid, row.projectUuid)
        continue
      }
      // treeValue を上方向に辿り、projectUuid を持つ祖先を探す
      for (let i = row.treeValue.length - 2; i >= 0; i--) {
        const ancestor = rowByUuid.get(row.treeValue[i])
        if (ancestor?.projectUuid) {
          map.set(row.uuid, ancestor.projectUuid)
          break
        }
      }
    }
    return map
  }, [dataApi.data])

  // projectPlanUuid からプロジェクト情報を解決するヘルパー
  const resolveProjectInfo = useCallback(
    (
      projectPlanUuid: string
    ): { name: string; iconUrl?: string } | undefined => {
      const projectUuid = projectPlanToProjectUuid.get(projectPlanUuid)
      if (projectUuid) {
        return projectInfoMap.get(projectUuid)
      }
      if (gridApi) {
        let foundProjectUuid: string | undefined
        gridApi.forEachNodeAfterFilterAndSort(node => {
          if (foundProjectUuid) return
          if (node.data?.uuid === projectPlanUuid && node.data?.projectUuid) {
            foundProjectUuid = node.data.projectUuid
          }
        })
        if (foundProjectUuid) {
          return projectInfoMap.get(foundProjectUuid)
        }
      }
      return undefined
    },
    [projectPlanToProjectUuid, projectInfoMap, gridApi]
  )

  // 同一プロジェクトの工程間ではプログラム側CPを作成できない
  // （同一プロジェクト内のCPはプロジェクト計画画面で設定すべきため）。
  const validateAddCriticalPathLink = useCallback(
    (link: AddCriticalPathLinkInput): boolean => {
      const fromPlanUuid = link.fromProjectPlanUuid
      const toPlanUuid = link.toProjectPlanUuid
      if (!fromPlanUuid || !toPlanUuid) return true
      const fromProjectUuid = projectPlanToProjectUuid.get(fromPlanUuid)
      const toProjectUuid = projectPlanToProjectUuid.get(toPlanUuid)
      if (fromProjectUuid !== undefined && fromProjectUuid === toProjectUuid) {
        dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({ id: 'global.warning.businessError' }),
            text: intl.formatMessage({
              id: 'programManagement.criticalPath.sameProjectError',
            }),
          })
        )
        return false
      }
      return true
    },
    [projectPlanToProjectUuid, dispatch]
  )

  // ダイアログ用アイテムの構築（プロジェクト情報付き）
  const mergedDialogItems = useMemo<WbsItemLinkSummary[]>(() => {
    const addProjectInfo = (
      items: WbsItemLinkSummary[],
      source: WbsItemLinkSource
    ): WbsItemLinkSummary[] =>
      items.map(item => {
        // プログラム側CPはレスポンスの fromProject/toProject 由来の名称を既に持つ。
        // 未設定（新規ドラッグ作成・cross-project）の場合のみグリッドから解決する。
        const fromResolved =
          item.fromProjectName !== undefined
            ? undefined
            : resolveProjectInfo(item.fromProjectPlanUuid ?? '')
        const toResolved =
          item.toProjectName !== undefined
            ? undefined
            : resolveProjectInfo(item.toProjectPlanUuid ?? '')
        return {
          ...item,
          source,
          fromProjectName: item.fromProjectName ?? fromResolved?.name,
          fromProjectIconUrl: item.fromProjectIconUrl ?? fromResolved?.iconUrl,
          toProjectName: item.toProjectName ?? toResolved?.name,
          toProjectIconUrl: item.toProjectIconUrl ?? toResolved?.iconUrl,
        }
      })

    const uniqueProgramDialogItems = programCpData.dialogItems.filter(
      d => !crossProjectCpUuids.has(d.uuid)
    )

    return [
      ...addProjectInfo(uniqueProgramDialogItems, WBS_ITEM_LINK_SOURCE.PROGRAM),
      ...addProjectInfo(
        crossProjectCpData.dialogItems,
        WBS_ITEM_LINK_SOURCE.PROJECT
      ),
    ]
  }, [
    programCpData.dialogItems,
    crossProjectCpData.dialogItems,
    resolveProjectInfo,
    crossProjectCpUuids,
  ])

  // フィルタオプション
  const filterOptions = useMemo<WbsItemLinkFilterOption[]>(() => {
    const options: WbsItemLinkFilterOption[] = [
      {
        label: intl.formatMessage({ id: 'wbsItemLink.filter.all' }),
        value: FILTER_ALL,
      },
      {
        label: intl.formatMessage({ id: 'wbsItemLink.filter.program' }),
        value: FILTER_PROGRAM,
        iconType: 'program',
      },
    ]
    for (const [, info] of projectInfoMap) {
      options.push({
        label: info.name,
        value: info.projectUuid,
        iconType: 'project',
        // 実アイコンが無い場合はダイアログ側でデフォルトアイコン（正方形）を表示する
        iconUrl: info.iconUrl,
        isDefaultIcon: !info.iconUrl,
      })
    }
    return options
  }, [projectInfoMap])

  const getFilterKeyForItem = useCallback(
    (item: WbsItemLinkSummary): string => {
      if (isProgramSource(item)) return FILTER_PROGRAM
      const pUuid = projectPlanToProjectUuid.get(item.fromProjectPlanUuid ?? '')
      return pUuid ?? FILTER_PROGRAM
    },
    [projectPlanToProjectUuid]
  )

  const filteredDialogItems = useMemo(() => {
    if (cpFilterValue === FILTER_ALL) return mergedDialogItems
    return mergedDialogItems.filter(
      item => getFilterKeyForItem(item) === cpFilterValue
    )
  }, [mergedDialogItems, cpFilterValue, getFilterKeyForItem])

  // ガントバー（予定線）のドラッグ編集確定: 行の予定日を更新して保存対象にする。
  // gridContext を安定させるため、最新の dataApi は ref 経由で参照する
  const dataApiRef = useRef(dataApi)
  dataApiRef.current = dataApi
  const onGanttBarDateEdited = useCallback(
    (
      row: ProgramManagementRow,
      dateTerm: { startDate?: string; endDate?: string }
    ) => {
      const api = dataApiRef.current
      const newData = api.data.map(r =>
        r.uuid === row.uuid
          ? {
              ...r,
              scheduledDate: dateTerm,
              edited: true,
              scheduledDateEdited: true,
              scheduledStartDateAggregated: false,
              scheduledEndDateAggregated: false,
            }
          : r
      )
      api.setDataAfterEditing(newData)
      api.refreshAggregation(newData)
    },
    []
  )

  const gridContext: ProgramManagementGridContext = useMemo(
    () => ({
      aggregateTarget: pageStateApi.aggregateTarget,
      aggregateField: pageStateApi.aggregateField,
      dateFormat: pageStateApi.dateFormat,
      workloadUnitState,
      isRowHeightFixed: pageStateApi.ganttVisible,
      ganttParameter: pageStateApi.ganttVisible
        ? pageStateApi.ganttParameter
        : undefined,
      ganttDisplayTimeScale: pageStateApi.ganttVisible
        ? displayTimeScale
        : undefined,
      ganttTimeScale: pageStateApi.ganttVisible ? timeScale : undefined,
      // イナズマ線表示中は当日線を灰色破線に落とす（GanttBackground が参照。ProjectPlanNew と同挙動）
      progressLineEnabled:
        pageStateApi.ganttVisible &&
        pageStateApi.ganttParameter.displayOptions.showProgressLine,
      onGanttBarDateEdited,
      errors: contextRef.current.errors,
      draggableNodeId: contextRef.current.draggableNodeId,
      onTree: contextRef.current.onTree,
    }),
    [
      pageStateApi.aggregateTarget,
      pageStateApi.aggregateField,
      pageStateApi.dateFormat,
      workloadUnitState,
      pageStateApi.ganttVisible,
      pageStateApi.ganttParameter,
      displayTimeScale,
      timeScale,
      onGanttBarDateEdited,
      contextRef,
    ]
  )

  // gridContext（timeScale 等を含む完全な context）を contextRef にも同期する。
  // updateContext（errors/draggableNodeId 等の部分更新）は contextRef を元に
  // grid context 全体を置き換えるため、同期しないと部分更新時に timeScale 等が
  // 失われ、以降に再レンダリングされたガントセルが空白になる。
  useEffect(() => {
    updateContext(gridContext)
  }, [gridContext, updateContext])

  // ガントチャート列の表示切替
  useEffect(() => {
    if (!gridApi) return
    const ganttColumn = gridApi.getColumn('ganttChart')
    if (!ganttColumn) return
    gridApi.setColumnsVisible(['ganttChart'], pageStateApi.ganttVisible)
  }, [gridApi, pageStateApi.ganttVisible])

  // コンテキスト変更時にセルとヘッダーを再描画する
  useEffect(() => {
    gridApi?.refreshCells({ force: true })
    gridApi?.refreshHeader()
  }, [gridApi, gridContext])

  // 行追加・並び替え・削除でデータが変わった時に行番号を採番し直す。
  // 行番号はツリー表示順から動的に計算しており、変更が無い行では valueGetter が
  // 再評価されないため、行番号列だけ明示的に再描画する（ProjectPlanNew と同方式）。
  // ツリーモデルの再構築後に実行するため次フレームへ遅延する。
  useEffect(() => {
    if (!gridApi) return
    const rafId = requestAnimationFrame(() => {
      gridApi.refreshCells({ columns: ['rowNumber'], force: true })
    })
    return () => cancelAnimationFrame(rafId)
  }, [gridApi, dataApi.data])

  // ガント表示の切替・タイムスケール生成時は redrawRows で行を再生成する。
  // refreshCells だけだとトグルで表示した直後にタイムスケール反映後の予定線などが
  // 描画されないため（ProjectPlanNew と同様に redrawRows を用いる）。
  useEffect(() => {
    if (!gridApi || !pageStateApi.ganttVisible) return
    gridApi.redrawRows()
    gridApi.refreshHeader()
  }, [gridApi, pageStateApi.ganttVisible, displayTimeScale, timeScale])

  // resetRowHeights は新しい rowHeight がグリッドに反映された後に実行する必要がある。
  // 同期実行すると古い行高のままになり、ガント列を表示しても予定線などが描画されない
  // （リロードでは正しい行高でマウントされるため表示される）。rAF で次フレームに遅延する。
  useEffect(() => {
    if (!gridApi) return
    const rafId = requestAnimationFrame(() => {
      gridApi.resetRowHeights()
    })
    return () => cancelAnimationFrame(rafId)
  }, [gridApi, pageStateApi.rowHeight])

  // プロジェクト行追加などで行数が変わった時、新規行にガント時の固定行高を適用する。
  // redrawRows は全行のDOMを作り直し、ガントのバー描画に使う計測幅が 0 になって
  // 全体が空白化するため使わない（新規行は AG Grid が自然に描画する）。行高の再適用のみ行う。
  const rowCount = dataApi.data.length
  useEffect(() => {
    if (!gridApi || !pageStateApi.ganttVisible) return
    const rafId = requestAnimationFrame(() => {
      gridApi.resetRowHeights()
    })
    return () => cancelAnimationFrame(rafId)
  }, [gridApi, pageStateApi.ganttVisible, rowCount])

  // クリティカルパスの変更（作成/編集/削除）時に保存要求を出す（SavePopper 表示）。
  // CP は onCellValueChanged を経由しないため、ここで明示的に検知する。
  // dialogItems/criticalPaths の更新後（ref 反映後）に実行され、未保存差分があれば requireSave。
  useEffect(() => {
    if (programCpData.hasUnsavedChanges()) {
      dispatch(requireSave())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- programCpData は毎レンダー新規オブジェクトのため、CP変更を示すstateスライス（dialogItems/criticalPaths）のみを依存に限定する
  }, [
    programCpData.dialogItems,
    programCpData.criticalPaths,
    programCpData.hasUnsavedChanges,
    dispatch,
  ])

  // --- 編集機能 ---

  // 既存プロジェクトUUID一覧（プロジェクト選択ダイアログで除外用）
  const existingProjectUuids = useMemo(
    () =>
      dataApi.data
        .filter(
          r => r.type === PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT && r.projectUuid
        )
        .map(r => r.projectUuid!),
    [dataApi.data]
  )

  // D&D
  const dragAndDrop = useProgramManagementDragAndDrop(
    ref,
    dataApi.data,
    dataApi.setDataAfterEditing,
    gridApi ?? undefined,
    updateContext,
    dataApi.refreshAggregation,
    contextRef
  )

  // コンテキストメニュー
  const { contextMenu } = useProgramManagementContextMenu({
    addGroupRowAction: () => {
      if (gridApi) {
        dataApi.addGroupRow(gridApi)
      }
    },
    addChildGroupRowAction: (parentRow: ProgramManagementRow) => {
      if (gridApi) {
        dataApi.addChildGroupRow(gridApi, parentRow)
      }
    },
    addProjectRowAction: (parentRow: ProgramManagementRow) => {
      dialog.openProjectSelection(parentRow)
    },
    addNewProjectRowAction: (parentRow: ProgramManagementRow) => {
      openProjectEditDialog({
        isNew: true,
        onComplete: formModel => {
          if (gridApi) {
            dataApi.addNewProjectRow(gridApi, parentRow, formModel)
          }
        },
      })
    },
    deleteRowAction: () => {
      dialog.openDeleteConfirmation()
    },
    // 新規プロジェクト追加メニューは project-v2 有効 かつ PROJECT_REGISTRATION 権限時のみ。
    showAddNewProject: projectEditDialogEnabled,
    allRows: dataApi.data,
  })

  // セル値変更時
  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<ProgramManagementRow>) => {
      dispatch(requireSave())
      // 予定日の変更時は集計をリフレッシュ
      if (
        event.colDef.colId === 'scheduledStartDate' ||
        event.colDef.colId === 'scheduledEndDate'
      ) {
        dataApi.refreshAggregation()
      }
    },
    [dispatch, dataApi]
  )

  // バリデーション
  const validateBeforeSubmit = useCallback((): boolean => {
    const targetRows = dataApi.data.filter(row => row.edited || row.added)
    const targetRowUuids = targetRows.map(row => row.uuid)
    const errors = validateNodes({
      gridApi,
      uuids: targetRowUuids,
      extraValidate: (colId, node, colDef) => {
        if (!node || !colId) return
        const validate = colDef.cellRendererParams?.validate
        if (!validate || !gridApi) return
        const val = gridApi.getCellValue({
          colKey: colId,
          rowNode: node,
        })
        return validate(val, {
          node,
          data: node.data,
          colDef,
          colId,
          api: gridApi,
        })
      },
    })

    // 階層構造チェック：GROUPはPROJECTの下におけない
    for (const row of targetRows) {
      if (programItemRowService.isGroupRow(row)) {
        const parentUuid = getParentUuid(row)
        if (parentUuid) {
          const parent = dataApi.data.find(r => r.uuid === parentUuid)
          if (parent && programItemRowService.isProjectRow(parent)) {
            errors.add({
              id: row.uuid,
              field: 'type',
              message: intl.formatMessage({
                id: 'programManagement.validation.groupUnderProject',
              }),
            })
          }
        }
      }
    }

    updateContext({ errors })

    if (errors.hasMessage()) {
      dispatch(
        addGlobalMessage({
          type: MessageLevel.WARN,
          title: intl.formatMessage({ id: 'global.warning.businessError' }),
          text: errors.toMessage(id => {
            const node = gridApi?.getRowNode(id)
            return node ? getRowNumber(node).toString() : ''
          }),
        })
      )
      return false
    }
    return true
  }, [dataApi.data, gridApi, updateContext, dispatch])

  // 保存処理
  const onSubmit = useCallback(async () => {
    if (!pageStateApi.selectedProgramUuid) return

    gridApi?.stopEditing()

    if (!validateBeforeSubmit()) return

    // 例外（HTTP 400 等）は throw され、未処理のままグローバルの unhandledrejection ハンドラ
    // （handleError）に伝播してエラー表示される（ProjectPlanNew と同じ方式）。
    // 2xx で返る warning は handleWarning で表示し、エラー/警告が無い場合のみ成功扱いにする。
    const findRowByUuid = (uuid: string) =>
      dataApi.data.find(v => v.uuid === uuid)

    // ツリー構造保存
    const treeResponse = await dataApi.save(pageStateApi.selectedProgramUuid)
    if (treeResponse.hasWarning) {
      handleWarning(
        extractValuesFromResponse(treeResponse.json, 'messages'),
        findRowByUuid
      )
    }

    // クリティカルパス保存
    const cpResponse = await programCpData.saveProgramCriticalPaths()
    if (cpResponse.hasWarning) {
      handleWarning(
        extractValuesFromResponse(cpResponse.json, 'messages'),
        findRowByUuid
      )
    }

    if (
      !treeResponse.hasError &&
      !treeResponse.hasWarning &&
      !cpResponse.hasError &&
      !cpResponse.hasWarning
    ) {
      dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({
            id: 'programManagement.save.success',
          }),
        })
      )
      dispatch(doNotRequireSave())
      await dataApi.reload()
    }
  }, [
    pageStateApi.selectedProgramUuid,
    gridApi,
    validateBeforeSubmit,
    dataApi,
    programCpData,
    dispatch,
    functionUuid,
  ])

  // リロード（未保存確認）
  const onReload = useCallback(() => {
    if (dataApi.hasChanged()) {
      dialog.openCancel()
    } else {
      dataApi.reload()
    }
  }, [dataApi, dialog])

  const handleReloadConfirm = useCallback(async () => {
    dialog.closeCancel()
    dispatch(doNotRequireSave())
    updateContext({
      errors: new InputError(),
      draggableNodeId: undefined,
      onTree: false,
    })
    await dataApi.reload()
  }, [dialog, dispatch, updateContext, dataApi])

  // Ctrl+S
  useKeyBind(
    [{ key: KEY_SAVE, fn: onSubmit, stopDefaultBehavior: true }],
    [onSubmit]
  )

  const onSwitchingGanttChart = useCallback(
    (isDisplayGantt: boolean) => {
      if (isDisplayGantt) {
        if (pageStateApi.rowHeight !== GANTT_MIN_ROW_HEIGHT) {
          rowHeightBeforeGantt.current = pageStateApi.rowHeight
          pageStateApi.setRowHeight(GANTT_MIN_ROW_HEIGHT)
        }
      } else {
        if (rowHeightBeforeGantt.current !== null) {
          pageStateApi.setRowHeight(rowHeightBeforeGantt.current)
          rowHeightBeforeGantt.current = null
        }
      }
      pageStateApi.setGanttVisible(isDisplayGantt)
    },
    [pageStateApi]
  )

  const handleOptimizeGanttColumnWidth = useCallback(() => {
    if (!gridApi) return
    const contentWidth = getGanttChartWidth(pageStateApi.ganttParameter)
    const viewportEl = document.querySelector<HTMLElement>(
      '.ag-center-cols-viewport'
    )
    if (!viewportEl) {
      gridApi.setColumnWidths([{ key: 'ganttChart', newWidth: contentWidth }])
      return
    }
    const otherColumnsWidth = gridApi
      .getDisplayedCenterColumns()
      .filter(c => c.getColId() !== 'ganttChart')
      .reduce((sum, c) => sum + c.getActualWidth(), 0)
    const availableGanttWidth = viewportEl.clientWidth - otherColumnsWidth
    if (availableGanttWidth <= 0) {
      gridApi.setColumnWidths([{ key: 'ganttChart', newWidth: contentWidth }])
      return
    }
    const minWidth =
      contentWidth *
      GANTT_FIT_MIN_CONTENT_RATIO[pageStateApi.ganttParameter.unit]
    const newWidth = Math.max(availableGanttWidth, minWidth)
    gridApi.setColumnWidths([{ key: 'ganttChart', newWidth }])
  }, [gridApi, pageStateApi.ganttParameter])

  const getGanttRowItemCallback = useCallback(
    (data: ProgramManagementRow) => getGanttRowItem(data, gridContext),
    [gridContext]
  )

  // 削除対象のラベル取得
  const getDeleteRowLabels = useCallback((): string[] => {
    if (!gridApi) return []
    return getSelectedNode(gridApi).map(
      (node: IRowNode<ProgramManagementRow>) => node.data?.displayName || ''
    )
  }, [gridApi])

  // 削除確認後の処理
  const handleDeleteConfirm = useCallback(() => {
    if (!gridApi) return
    const selectedNodes = getSelectedNode(gridApi)
    const selectedRows = selectedNodes
      .map(n => n.data)
      .filter((d): d is ProgramManagementRow => d !== undefined)

    // 削除対象（選択行＋子孫）の工程 wbsItemUuid を集約し、端点が消えるCPを除去する。
    // これをしないと、未保存の新規CPがサーバー登録され、紐づきチェックでエラーになる。
    const deletedUuids = new Set(selectedRows.map(r => r.uuid))
    const removedWbsItemUuids = new Set(
      dataApi.data
        .filter(
          r =>
            deletedUuids.has(r.uuid) ||
            r.treeValue.some(v => deletedUuids.has(v) && v !== r.uuid)
        )
        .map(r => r.wbsItemUuid)
        .filter((u): u is string => !!u)
    )
    programCpData.removeByWbsItemUuids(removedWbsItemUuids)

    dataApi.deleteRows(selectedRows)
    dialog.closeDeleteConfirmation()
  }, [gridApi, dataApi, dialog, programCpData])

  // プロジェクト選択後の処理
  const handleProjectSelect = useCallback(
    async (
      projects: {
        uuid: string
        code: string
        displayName: string
        iconUrl?: string
      }[]
    ) => {
      if (!gridApi || !dialog.projectSelectionParent) return
      // 例外はグローバルの unhandledrejection ハンドラ（handleError）に伝播して表示される
      await dataApi.addProjectRows(
        gridApi,
        dialog.projectSelectionParent,
        projects
      )
    },
    [gridApi, dialog.projectSelectionParent, dataApi]
  )

  return (
    <PageArea>
      <SavePopper loading={dataApi.loading} onSubmit={onSubmit} />
      <Header
        programs={dataApi.programs}
        selectedProgramUuid={pageStateApi.selectedProgramUuid}
        onSelectProgram={pageStateApi.setSelectedProgramUuid}
        onReload={onReload}
        pageStateApi={pageStateApi}
        isRowHeightFixed={pageStateApi.ganttVisible}
        onSwitchingGanttChart={onSwitchingGanttChart}
        filteredColumns={bulkSheetStateApi.filteredColumns}
        onDeleteFilteredColumn={bulkSheetStateApi.onDeleteFilteredColumn}
        resetFilters={bulkSheetStateApi.resetFilters}
        sortColumnsState={bulkSheetStateApi.sortColumnsState}
        onDeleteSortedColumn={bulkSheetStateApi.onDeleteSortedColumn}
        onDeleteSortedAllColumns={bulkSheetStateApi.onDeleteSortedAllColumns}
        onChangeSortColumnState={bulkSheetStateApi.onChangeSortColumnState}
        onClickColumnSettingButton={columnSetting.toggle}
        columnSettingOpen={columnSetting.isOpen}
        isDependencyCriticalPathEnabled={isDependencyCriticalPathEnabled}
        onOpenCriticalPathDialog={() => setCriticalPathDialogOpen(true)}
        onOptimizeGanttColumnWidth={handleOptimizeGanttColumnWidth}
      />
      <GanttChartProvider
        containerRef={ref}
        criticalPaths={mergedCriticalPaths}
        onCriticalPathsChange={programCpData.onCriticalPathsChange}
        cpWbsItemInfoMap={mergedWbsItemInfoMap}
        validateAddCriticalPathLink={validateAddCriticalPathLink}
      >
        <BulkSheetView
          ref={ref}
          // TODO(全画面共通化): 行ホバー/選択色を全画面共通にする際は、この
          //   rootClassName（agGrid.scss のスコープ用クラス）を削除する。
          rootClassName="program-management-grid"
          // TODO(全画面共通化): 選択行の枠線を全画面共通にしたら、この prop は不要になる
          //   （BulkSheetView 側で常時有効化する）。
          selectionRowFrameEnabled={true}
          gridOptions={gridOptions}
          context={gridContext}
          rowData={dataApi.data}
          rowHeight={pageStateApi.rowHeight}
          onGridReady={onGridReady}
          onFirstDataRendered={bulkSheetStateApi.onFirstDataRendered}
          onSortChanged={bulkSheetStateApi.onSortChanged}
          onColumnMoved={bulkSheetStateApi.onColumnMoved}
          onColumnResized={bulkSheetStateApi.onColumnResized}
          onColumnVisible={bulkSheetStateApi.onColumnVisible}
          onFilterChanged={bulkSheetStateApi.onFilterChanged}
          getContextMenuItems={contextMenu}
          onRowDragEnter={dragAndDrop.onRowDragEnter}
          onRowDragMove={dragAndDrop.onRowDragMove}
          onRowDragEnd={dragAndDrop.onRowDragEnd}
          onRowDragLeave={dragAndDrop.refreshDragStyle}
          onCellValueChanged={onCellValueChanged}
        />
        {pageStateApi.ganttVisible &&
          gridApi &&
          timeScale.length > 0 &&
          pageStateApi.ganttParameter.displayOptions.showProgressLine && (
            <ProgressLineOverlay
              gridApi={gridApi}
              timeScale={timeScale}
              aggregateTarget={pageStateApi.aggregateTarget}
              aggregateField={pageStateApi.aggregateField}
              unit={pageStateApi.ganttParameter.unit}
              getGanttRowItem={getGanttRowItemCallback}
            />
          )}
      </GanttChartProvider>
      <ColumnSettingPopper
        open={columnSetting.isOpen}
        anchorEl={columnSetting.anchorEl}
        close={columnSetting.close}
        gridApi={gridApi ?? undefined}
        height={ref.current?.offsetHeight}
        initializeColumnState={() => {
          gridApi?.resetColumnState()
          gridApi?.setFilterModel(null)
          bulkSheetStateApi.saveColumnState([])
          bulkSheetStateApi.saveFilterState({})
          bulkSheetStateApi.resetDerivedState()
        }}
        applicationFunctionUuid={functionUuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={bulkSheetStateApi.columnState}
        offset={90}
      />
      <Loading isLoading={dataApi.loading} elem={ref.current} />
      <WbsItemLinkDialog
        open={criticalPathDialogOpen}
        onClose={() => setCriticalPathDialogOpen(false)}
        type="criticalPath"
        items={filteredDialogItems}
        onUpdateLink={programCpData.updateLinkFromDialog}
        onRemoveLink={programCpData.removeLinkFromDialog}
        filterOptions={filterOptions}
        activeFilter={cpFilterValue}
        onFilterChange={setCpFilterValue}
      />
      {dialog.deleteConfirmation && gridApi && (
        <DeleteRowConfirmationDialog
          target={getDeleteRowLabels()}
          onConfirm={handleDeleteConfirm}
          onClose={dialog.closeDeleteConfirmation}
        />
      )}
      {dialog.cancel && (
        <CancelConfirmDialog
          open={true}
          onConfirm={handleReloadConfirm}
          onClose={dialog.closeCancel}
        />
      )}
      <ProjectSelectionDialog
        open={dialog.projectSelection}
        onClose={dialog.closeProjectSelection}
        onSelect={handleProjectSelect}
        existingProjectUuids={existingProjectUuids}
      />
    </PageArea>
  )
}

export const ProgramManagement = pageComponent(ProgramManagementPage)
